/* eslint-disable no-undefined,no-param-reassign */
/* global G */
import { def } from 'lib/util'

/**
 * Sequence Component State Factory.
 *
 * Returns an object with methods get, set, unset, undefine, nullify and toggle that allows to
 * easily modify the `key` state property of an object passed to those functions.
 *
 * @param {Symbol|string} key the name of the state property of obj to be modified
 * @returns {Object}
 */
const sequenceComponentState = (key) => {
  /**
   * Sets `value` as the value of the state of obj identified by `key`.
   *
   * @param {Object} obj  ui component
   * @param {any} value   the value to set
   * @returns {Object}    the `obj` parameter
   * @private
   */
  const _set = (obj, value) => {
    const objState = obj[G.STATE]
    objState[key] = value
    return obj
  }

  /**
   * Returns the value of the `key` state of `obj` or the value of its prop, if state is not defined
   * (i.e. the initial state).
   *
   * @param {Object} obj        ui component
   * @param {boolean} stateOnly whether to look only for the state value
   * @returns {any}             the value of the checked state of `obj`.
   */
  const get = (obj, stateOnly = false) => {
    const state = obj[G.STATE]?.[key]
    const prop = obj[G.PROPS]?.[key]
    return def(state) || stateOnly ? state : prop
  }

  /**
   * Sets the `key` state of `obj` to `value` or `true`.
   *
   * @param {Object} obj ui component
   * @param {any} [value=true] the value to set
   * @returns {Object}    the `obj` parameter
   */
  const set = (obj, value = true) => _set(obj, value)

  /**
   * Sets the `key` state of `obj` to `false`.
   *
   * @param {Object} obj  ui component
   * @returns {Object}    the `obj` parameter
   */
  const unset = obj => _set(obj, false)

  /**
   * Sets the `key` state of `obj` to `undefined`.
   *
   * @param {Object} obj  ui component
   * @returns {Object}    the `obj` parameter
   */
  const undefine = obj => _set(obj, undefined)

  /**
   * Sets the `key` state of `obj` to `null`.
   *
   * @param {Object} obj  ui component
   * @returns {Object}    the `obj` parameter
   */
  const nullify = obj => _set(obj, null)

  /**
   * Deletes the `key` state of `obj`.
   *
   * @param {Object} obj ui component
   */
  const clear = (obj) => {
    delete obj[G.STATE][key]
    return obj
  }

  /**
   * Toggles the value of the `key` state of `obj` between `true` or `false`.
   *
   * @param {Object} obj  ui component
   * @returns {Object}    the `obj` parameter
   */
  const toggle = obj => _set(obj, !obj[G.STATE][key])

  return {
    get,
    set,
    unset,
    undefine,
    nullify,
    clear,
    toggle,
  }
}

export default sequenceComponentState
